import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type {
  CloudflareAccessApplication,
  CloudflareAccessGroup,
  CloudflareReusablePolicy,
  CloudflareZeroTrustListType,
} from "../shared/types";
import { adminQueries } from "./admin-query";
import { CapabilityBadge, EmptyState } from "./AdminUi";
import { Icon } from "./Icon";

const LIST_TYPES: Array<Exclude<CloudflareZeroTrustListType, "UNKNOWN">> = [
  "DOMAIN",
  "EMAIL",
  "IP",
  "URL",
  "DEVICE",
  "SERIAL",
  "AAGUID",
  "CATEGORY",
  "LOCATION",
];

function plural(count: number, singular: string, pluralValue = `${singular}s`) {
  return `${count} ${count === 1 ? singular : pluralValue}`;
}

function decisionTone(decision: CloudflareReusablePolicy["decision"]) {
  return decision === "allow" || decision === "non_identity"
    ? "positive"
    : decision === "deny" || decision === "bypass" || decision === "unknown"
      ? "warning"
      : "";
}

function selectorLabel(selector: string): string {
  return selector.replaceAll("_", " ");
}

function applicationGroupReferences(
  applications: CloudflareAccessApplication[],
): Map<string, Set<string>> {
  const references = new Map<string, Set<string>>();
  for (const application of applications) {
    for (const policy of application.policies) {
      for (const rule of [
        ...policy.include,
        ...policy.require,
        ...policy.exclude,
      ]) {
        const group = rule.group;
        if (!group || typeof group !== "object" || Array.isArray(group)) {
          continue;
        }
        const groupId = (group as Record<string, unknown>).id;
        if (typeof groupId !== "string" || !groupId) continue;
        const existing = references.get(groupId) ?? new Set<string>();
        existing.add(`application:${application.id}:policy:${policy.id}`);
        references.set(groupId, existing);
      }
    }
  }
  return references;
}

function capabilityState(
  states: Array<"available" | "degraded" | "not_configured" | undefined>,
  failed: boolean,
) {
  if (failed || states.includes("degraded")) return "degraded" as const;
  return states.length > 0 &&
    states.every((state) => state === "not_configured")
    ? ("not_configured" as const)
    : ("available" as const);
}

export function CloudflareReusableControlsSection({
  applications,
}: {
  applications: CloudflareAccessApplication[];
}) {
  const [groupQuery, setGroupQuery] = useState("");
  const [groupSearch, setGroupSearch] = useState("");
  const [listType, setListType] = useState<
    "all" | Exclude<CloudflareZeroTrustListType, "UNKNOWN">
  >("all");
  useEffect(() => {
    const timeout = window.setTimeout(
      () => setGroupSearch(groupQuery.trim()),
      300,
    );
    return () => window.clearTimeout(timeout);
  }, [groupQuery]);

  const policiesQuery = useInfiniteQuery(
    adminQueries.cloudflareReusablePolicies(20),
  );
  const groupsQuery = useInfiniteQuery(
    adminQueries.cloudflareAccessGroups(20, groupSearch || undefined),
  );
  const listsQuery = useQuery(adminQueries.cloudflareZeroTrustLists());

  const policyPages = policiesQuery.data?.pages ?? [];
  const groupPages = groupsQuery.data?.pages ?? [];
  const policies = [
    ...new Map(
      policyPages
        .flatMap((page) => page.policies)
        .map((policy) => [policy.id, policy]),
    ).values(),
  ];
  const groups = [
    ...new Map(
      groupPages
        .flatMap((page) => page.groups)
        .map((group) => [group.id, group]),
    ).values(),
  ];
  const lists = listsQuery.data?.lists ?? [];
  const visibleLists =
    listType === "all" ? lists : lists.filter((list) => list.type === listType);
  const policyTotal = policyPages[0]?.total ?? policies.length;
  const groupTotal = groupPages[0]?.total ?? groups.length;
  const listTotal = listsQuery.data?.total ?? lists.length;
  const capability = capabilityState(
    [
      policyPages[0]?.capability,
      groupPages[0]?.capability,
      listsQuery.data?.capability,
    ].filter((state) => state !== undefined),
    policiesQuery.isError || groupsQuery.isError || listsQuery.isError,
  );

  const groupReferences = useMemo(() => {
    const references = applicationGroupReferences(applications);
    for (const policy of policies) {
      for (const groupId of policy.accessGroupIds) {
        const existing = references.get(groupId) ?? new Set<string>();
        existing.add(`reusable:${policy.id}`);
        references.set(groupId, existing);
      }
    }
    return references;
  }, [applications, policies]);

  const hasError =
    policiesQuery.isError || groupsQuery.isError || listsQuery.isError;

  return (
    <section className="directory-card cloudflare-section reusable-control-section">
      <header className="collection-header">
        <div>
          <h2>Reusable controls</h2>
          <p>Shared Access policies, rule groups, and Zero Trust lists.</p>
        </div>
        <div className="collection-header__actions">
          <CapabilityBadge capability={capability} />
          <span className="collection-header__count">
            {policyTotal + groupTotal + listTotal}
          </span>
        </div>
      </header>

      {hasError && (
        <div
          className="notice notice--warning reusable-control-notice"
          role="status"
        >
          Reusable controls could not be fully refreshed. Check Access Groups,
          Apps and Policies, and Zero Trust Read permissions.
        </div>
      )}

      <div className="reusable-control-grid">
        <section
          className="reusable-control-column"
          aria-labelledby="reusable-policies-heading"
        >
          <header>
            <div>
              <h3 id="reusable-policies-heading">Access policies</h3>
              <p>One policy definition shared across applications.</p>
            </div>
            <span className="panel-status">{policyTotal}</span>
          </header>
          {policies.length ? (
            <ul className="reusable-resource-list">
              {policies.map((policy) => {
                const tone = decisionTone(policy.decision);
                return (
                  <li key={policy.id}>
                    <div className="reusable-resource-heading">
                      <strong>{policy.name}</strong>
                      <span
                        className={`panel-status${tone ? ` panel-status--${tone}` : ""}`}
                      >
                        {policy.decision === "non_identity"
                          ? "service auth"
                          : policy.decision}
                      </span>
                    </div>
                    <p>
                      {plural(policy.appCount, "application")} ·{" "}
                      {plural(policy.ruleCounts.include, "include rule")}
                    </p>
                    <div className="reusable-selector-list">
                      {policy.selectors.map((selector) => (
                        <span key={selector}>{selectorLabel(selector)}</span>
                      ))}
                    </div>
                    <small>
                      {policy.sessionDuration
                        ? `${policy.sessionDuration} session`
                        : "Application session default"}
                      {policy.approvalRequired ? " · approval required" : ""}
                      {policy.purposeJustificationRequired
                        ? " · justification required"
                        : ""}
                    </small>
                  </li>
                );
              })}
            </ul>
          ) : policiesQuery.isPending ? (
            <div className="reusable-control-state" role="status">
              <span className="spinner spinner--dark" /> Loading policies…
            </div>
          ) : (
            <EmptyState
              compact
              hint="Create a reusable policy in Cloudflare Access."
              title="No reusable policies"
            />
          )}
          {policiesQuery.hasNextPage && (
            <footer className="reusable-control-footer">
              <button
                className="button button--quiet button--compact"
                disabled={policiesQuery.isFetchingNextPage}
                onClick={() => void policiesQuery.fetchNextPage()}
                type="button"
              >
                {policiesQuery.isFetchingNextPage
                  ? "Loading policies…"
                  : "Load more policies"}
              </button>
            </footer>
          )}
        </section>

        <section
          className="reusable-control-column"
          aria-labelledby="reusable-groups-heading"
        >
          <header>
            <div>
              <h3 id="reusable-groups-heading">Access rule groups</h3>
              <p>Reusable selectors, separate from directory groups.</p>
            </div>
            <span className="panel-status">{groupTotal}</span>
          </header>
          <div className="reusable-control-search-wrap">
            <label className="search-field reusable-control-search">
              <span className="sr-only">Search Access rule groups</span>
              <Icon name="search" size={16} />
              <input
                aria-label="Search Access rule groups"
                onChange={(event) => setGroupQuery(event.target.value)}
                placeholder="Search rule groups"
                type="search"
                value={groupQuery}
              />
            </label>
          </div>
          {groups.length ? (
            <ul className="reusable-resource-list">
              {groups.map((group: CloudflareAccessGroup) => {
                const references = groupReferences.get(group.id)?.size ?? 0;
                return (
                  <li key={group.id}>
                    <div className="reusable-resource-heading">
                      <strong>{group.name}</strong>
                      <span
                        className={`panel-status${references ? " panel-status--positive" : " panel-status--warning"}`}
                      >
                        {references
                          ? plural(references, "policy reference")
                          : "unreferenced"}
                      </span>
                    </div>
                    <p>
                      {plural(group.ruleCounts.include, "include rule")} ·{" "}
                      {plural(group.ruleCounts.require, "require rule")} ·{" "}
                      {plural(group.ruleCounts.exclude, "exclude rule")}
                    </p>
                    <div className="reusable-selector-list">
                      {group.selectors.map((selector) => (
                        <span key={selector}>{selectorLabel(selector)}</span>
                      ))}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : groupsQuery.isPending ? (
            <div className="reusable-control-state" role="status">
              <span className="spinner spinner--dark" /> Loading rule groups…
            </div>
          ) : (
            <EmptyState
              compact
              hint={
                groupSearch
                  ? "Try another rule-group name."
                  : "Create an Access group to reuse selectors."
              }
              onClearFilters={
                groupSearch
                  ? () => {
                      setGroupQuery("");
                      setGroupSearch("");
                    }
                  : undefined
              }
              title={groupSearch ? "No matching rule groups" : "No rule groups"}
            />
          )}
          {groupsQuery.hasNextPage && (
            <footer className="reusable-control-footer">
              <button
                className="button button--quiet button--compact"
                disabled={groupsQuery.isFetchingNextPage}
                onClick={() => void groupsQuery.fetchNextPage()}
                type="button"
              >
                {groupsQuery.isFetchingNextPage
                  ? "Loading rule groups…"
                  : "Load more rule groups"}
              </button>
            </footer>
          )}
        </section>

        <section
          className="reusable-control-column"
          aria-labelledby="zero-trust-lists-heading"
        >
          <header>
            <div>
              <h3 id="zero-trust-lists-heading">Zero Trust lists</h3>
              <p>Metadata only; list entries remain in Cloudflare.</p>
            </div>
            <span className="panel-status">{listTotal}</span>
          </header>
          <label className="select-field reusable-control-filter">
            <span>List type</span>
            <select
              onChange={(event) =>
                setListType(
                  event.target.value as
                    "all" | Exclude<CloudflareZeroTrustListType, "UNKNOWN">,
                )
              }
              value={listType}
            >
              <option value="all">All types</option>
              {LIST_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>
          {visibleLists.length ? (
            <ul className="reusable-resource-list">
              {visibleLists.map((list) => (
                <li key={list.id}>
                  <div className="reusable-resource-heading">
                    <strong>{list.name}</strong>
                    <span className="panel-status">{list.type}</span>
                  </div>
                  {list.description && <p>{list.description}</p>}
                  <small>{plural(list.count, "entry", "entries")}</small>
                </li>
              ))}
            </ul>
          ) : listsQuery.isPending ? (
            <div className="reusable-control-state" role="status">
              <span className="spinner spinner--dark" /> Loading lists…
            </div>
          ) : (
            <EmptyState
              compact
              hint={
                listType === "all"
                  ? "Create a list in Cloudflare reusable components."
                  : `No ${listType.toLowerCase()} lists are configured.`
              }
              onClearFilters={
                listType === "all" ? undefined : () => setListType("all")
              }
              title={
                listType === "all" ? "No Zero Trust lists" : "No matching lists"
              }
            />
          )}
        </section>
      </div>
    </section>
  );
}
